/**
 * Finance2RouteBoundary — immediate loading feedback when moving between
 * Finance pages.
 *
 * Every Finance page is a lazy chunk. React Router 7 navigates inside
 * `startTransition`, and a transition never swaps an already-revealed Suspense
 * boundary back to its fallback: the URL changes, the OLD page stays on screen
 * and looks frozen until the new chunk arrives. A boundary that is NEW is not
 * held back that way — its fallback shows at once.
 *
 * So this boundary is keyed on the page (the path segment after /finance2). A
 * different page gets a fresh boundary, hence the skeleton paints on the click.
 * Moves inside one page (reports → reports/:id, setup sub-screens) keep the key,
 * so nothing remounts that did not before.
 *
 * Scoped to Finance2 on purpose: the transition behaviour everywhere else in the
 * app is left as it was. The fallback is the same DashboardSkeleton Finance2Shell
 * draws while the scope loads, so the hand-off from "chunk loading" to "scope
 * loading" is one continuous placeholder, not two.
 */

import React, { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { DashboardSkeleton } from '@/components/base/Skeleton';
import Finance2NavProgress from './Finance2NavProgress';
import { TallyReturnBar } from './tally/tallyReturn';

/** `/app/maud-erp/finance2/<page>/...` → `<page>` ('' for the index). */
const pageKey = (pathname: string): string => {
  const m = pathname.match(/\/finance2\/?([^/]*)/);
  return m ? m[1] : pathname;
};

/*
 * Every Finance page draws its scrollbars in the masthead gold rather than the
 * app-wide violet — except Easy Entry Mode, which keeps its own teal screen.
 * Set on <html>, not the page, so the popups — portalled to <body> — follow too.
 */
const OWN_SCROLL_PAGES = new Set(['tally-entry']);
const FINANCE_SCROLL_CLASS = 'f2-voucher-scroll';

const Finance2RouteBoundary: React.FC = () => {
  const { pathname } = useLocation();
  const goldScroll = !OWN_SCROLL_PAGES.has(pageKey(pathname));

  useEffect(() => {
    if (!goldScroll) return undefined;
    const root = document.documentElement;
    root.classList.add(FINANCE_SCROLL_CLASS);
    return () => root.classList.remove(FINANCE_SCROLL_CLASS);
  }, [goldScroll]);

  return (
    <>
    <Finance2NavProgress />
    {/* "Back to Easy Entry" on any page Easy Entry opened — masters included, which have no Finance2Shell. */}
    <TallyReturnBar />
    <Suspense
      key={pageKey(pathname)}
      fallback={
        <Box sx={{ p: 3 }}>
          <DashboardSkeleton />
        </Box>
      }
    >
      <Outlet />
    </Suspense>
    </>
  );
};

export default Finance2RouteBoundary;
